import { Box, Flex } from "grid-styled";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { themeGet } from "styled-system";
import system from "system-components";

import { StyledFontAwesomeIcon } from "pstat-anywhere/components/partials/icons";
import theme from "pstat-anywhere/themes/policystat/theme";
import InlineFieldSet from "pstat-design-system/InlineFieldset";
import { TextInputWithIcon } from "pstat-design-system/inputs/TextInput";
import { Button } from "pstat-design-system/shared/Button";
import { H2 } from "pstat-design-system/text";
import { Text } from "pstat-design-system/text/paragraph";

export const PageContainer = system({
  px: [0, 0, 4]
});

export const PaginationContainer = system({
  mt: 4
});

export const PointerBox = styled(Box)`
  &:hover {
    cursor: pointer;
  }
`;

export const ActionButtonContainer = system({
  width: "100%",
  bg: "secondary.0",
  borderBottom: 1,
  borderRadius: 1,
  borderColor: "nav.80",
  boxShadow: "card",
  height: 60,
  display: "flex",
  pl: [2, 2, 8],
  pr: [2, 2, 8]
}).extend`
  border-bottom-left-radius: 0px;
  border-bottom-right-radius: 0px;
`;

const SearchInlineFieldSet = system({
  is: InlineFieldSet,
  width: 1,
  pb: 2
});

export const SearchBar = props => {
  const { t } = useTranslation();
  const [value, setValue] = useState(props.value);
  const timeout = useRef(null);
  const onChange = event => {
    const value = event.target.value;
    setValue(value);
  };
  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      props.onChange(value);
    }, 2000);
    return () => clearTimeout(timeout.current);
  }, [value]);
  return (
    <SearchInlineFieldSet>
      <Flex
        width="100%"
        alignItems={["stretch", "center", "center"]}
        flexDirection={["column", "row", "row"]}
      >
        <TextInputWithIcon
          icon={["far", "search"]}
          side="left"
          placeholder={props.placeholder}
          value={value}
          onChange={onChange}
          containerProps={{ flex: 1 }}
          aria-label={t("documentReports.advancedReportPage.filters.keyword")}
        />
        {props.children}
      </Flex>
    </SearchInlineFieldSet>
  );
};

const FilterDropdownRelativeContainer = system({
  position: "relative"
});

const FilterDropdownContainer = system({
  position: "absolute",
  right: 0,
  zIndex: theme.layers.tertiaryNav
}).extend`
  display: ${props => (props.expanded ? "block" : "none")};

  &:before,
  &:after {
    content: " ";
    height: 0;
    position: absolute;
    width: 0;
    border: 8px solid transparent; /* arrow size */
  }

  &:before {
    border-bottom-color: ${themeGet("colors.nav.100")}; /* arrow color */

    /* positioning */
    position: absolute;
    top: -6px;

    right: 16px;
    z-index: 2;
  }

  &:after {
    border-bottom-color: ${themeGet("colors.nav.90")}; /* arrow color */

    /* positioning */
    position: absolute;
    top: -8px;
    right: 16px;
    z-index: 1;
  }
`;

const FilterDropdownContent = system({
  mt: 2,
  bg: "nav.100",
  border: 1,
  borderColor: "nav.90",
  borderRadius: 1,
  boxShadow: "dropdown",
  minWidth: [0, 300, 300]
}).extend`
  display: ${props => (props.expanded ? "block" : "none")};
`;

const FilterDropdownHeaderContainer = system({
  is: Flex,
  justifyContent: "space-between",
  alignItems: "center",
  width: 1,
  pl: 5,
  pr: 4,
  py: 4,
  borderBottom: 2,
  borderColor: "nav.80"
});

const CloseButtonContainer = system({}).extend`
  cursor: pointer;
`;

const CloseButton = props => (
  <CloseButtonContainer
    tabIndex="0"
    role="button"
    aria-label={props.t("documentReports.advancedReportPage.filters.close")}
    data-testid="filter-header-close-button"
    onClick={props.onClick}
    onKeyDown={props.onKeyDown}
  >
    <StyledFontAwesomeIcon
      icon={["fal", "times"]}
      size="lg"
      color="nav.55"
      aria-label="close"
      ml={0}
      mr={0}
    />
  </CloseButtonContainer>
);

const FilterDropdownFooter = system({
  is: Flex,
  width: 1,
  px: 5,
  py: 3,
  justifyContent: "flex-end",
  bg: "nav.95",
  borderRadius: 1
});

export const FilterDropdown = props => {
  const { t } = useTranslation();
  const handleKeyDown = event => {
    if (event.key === "Enter") {
      props.onClose();
    }
  };
  return (
    <FilterDropdownRelativeContainer className="fieldset-button-container">
      <Button
        buttonStyle="tertiary"
        onClick={() => props.onToggle()}
        width={1}
        focusBorderProps={{ width: 1 }}
      >
        {props.filterName}
      </Button>
      <FilterDropdownContainer expanded={props.expanded}>
        <FilterDropdownContent expanded={props.expanded}>
          {props.expanded && (
            <Fragment>
              <FilterDropdownHeaderContainer>
                <H2 color="nav.0" fontWeight={theme.fontWeights.normal}>
                  {props.filterName}
                </H2>
                <CloseButton
                  onClick={props.onClose}
                  onKeyDown={handleKeyDown}
                  t={t}
                />
              </FilterDropdownHeaderContainer>
              <Flex flexDirection="column" px={5} py={4}>
                {props.children}
              </Flex>
              <FilterDropdownFooter>
                <Button buttonStyle="primary" onClick={props.onClose} px={6}>
                  {t("documentReports.advancedReportPage.filters.done")}
                </Button>
              </FilterDropdownFooter>
            </Fragment>
          )}
        </FilterDropdownContent>
      </FilterDropdownContainer>
    </FilterDropdownRelativeContainer>
  );
};

const ActiveFilterBoxContainer = system({
  display: "inline-block",
  bg: "nav.90",
  p: 1,
  borderRadius: 1,
  mr: 1,
  mb: 1
});

const CloseIcon = system({
  is: StyledFontAwesomeIcon,
  mr: 0
}).extend`
  cursor: pointer;
`;

export const ActiveFilterBox = ({ children, onClick }) => {
  const handleKeyDown = event => {
    if (event.key === "Enter") {
      onClick();
    }
  };
  return (
    <ActiveFilterBoxContainer>
      <Text fontSize={1} fontWeight="medium">
        {children}
        <CloseIcon
          icon={["fas", "times-circle"]}
          role="button"
          onClick={onClick}
          onKeyDown={handleKeyDown}
          tabIndex="0"
        />
      </Text>
    </ActiveFilterBoxContainer>
  );
};
